import type { Step as RcStep } from '@sb/remote-control/types';
import type { StepKind, Space } from '@sb/routine-runner';

import { type ValidationRule } from '../../types';
import { ActionRequiredError } from '../../util/errors';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';

const permittedParentStepKinds = ['MoveArmToV2', 'Weld'];

class WaypointRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.Waypoint', 'Waypoint');
  }

  protected validate({ parents, args }: CanonicalValidateArgs): void {
    type WaypointArgs = Extract<RcStep.Arguments, { argumentKind: 'Waypoint' }>;
    const a = args as WaypointArgs;
    const parentStepKind = this.getParentKind(parents);
    this.checkParentKind(parentStepKind);
    this.checkTcpOption(parentStepKind, a);
    this.checkTarget(a);
  }

  /**
   * Get the immediate parent kind (if any).
   *
   * @returns {string} Parent stepKind or empty string
   */
  private getParentKind(
    parents: Array<{ stepKind: StepKind | string }>,
  ): string {
    return parents?.[0]?.stepKind ?? '';
  }

  /**
   * Ensure parent kind is allowed for Waypoint.
   * Throws ActionRequiredError if invalid.
   *
   * @throws {ActionRequiredError}
   */
  private checkParentKind(parentStepKind: string): void {
    if (!permittedParentStepKinds.includes(parentStepKind)) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message:
          'Waypoint steps may only be used inside a Move Arm or Weld step',
      });
    }
  }

  /**
   * Validate TCP option when parent is Weld.
   * Throws ActionRequiredError if invalid.
   *
   * @throws {ActionRequiredError}
   */
  private checkTcpOption(
    parentStepKind: string,
    args: Extract<RcStep.Arguments, { argumentKind: 'Waypoint' }>,
  ): void {
    if (parentStepKind === 'Weld' && args.tcpOption !== 'auto') {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message:
          'TCP Option must be set to "auto" when used within a Weld step',
        fieldId: 'tcpOption',
      });
    }
  }

  /**
   * Ensure target pose or jointAngles are configured when required.
   * Throws ActionRequiredError if missing.
   *
   * @throws {ActionRequiredError}
   */
  private checkTarget(
    args: Extract<RcStep.Arguments, { argumentKind: 'Waypoint' }>,
  ): void {
    const target = args.target as Space.Position | null;

    const hasTarget =
      !!target && (target.jointAngles != null || target.pose != null);

    if (args.targetKind === 'singlePosition' && !hasTarget) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Joint angles or pose must be set',
        fieldId: 'jointAngles',
      });
    }
  }
}

export const WaypointValidation: ValidationRule =
  new WaypointRule().toValidationRule();
